import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle } from 'lucide-react';

const ASSETS = {
  about1: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=800",
  about2: "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&w=800",
  services: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=800",
  team: "https://images.pexels.com/photos/32588548/pexels-photo-32588548.jpeg?auto=compress&cs=tinysrgb&w=800",
};

const whyChooseUs = [
  'Experienced and Certified Technicians',
  'Energy-Efficient Heating and Cooling Solutions',
  'Certified A+ Rating with the Better Business Bureau',
  '4.8+ Rating on Google Reviews',
  'Multi-Year NextDoor Neighborhood Faves Winner',
  'Locally Trusted Since 1976',
];

const OurCompany = () => {
  return (
    <div data-testid="our-company-page">
      {/* About BNPH Section */}
      <section className="py-16 bg-white" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-6 leading-tight">
                About BNPH Services
              </h2>
              <p className="text-gray-600 mb-5 leading-relaxed">
                Bruce Nelson Plumbing & Heating Services, located in St. Paul, MN, has been servicing Minneapolis, St. Paul, and the surrounding area since 1976. We are the metro's trusted contractor for plumbing, heating, and air conditioning services.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <img 
                src={ASSETS.about1} 
                alt="BNPH Team" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Serving Residential and Commercial */}
      <section className="py-16 bg-[#F8F8F8]" data-testid="residential-commercial-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={ASSETS.about2} 
                alt="Plumber working" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-6 leading-tight">
                Serving Residential and<br />Commercial Needs
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Whether your needs are residential or commercial, we can help. From new construction to remodeling or general maintenance and repair, we want to be your contractor.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expert Services Section - No Image, just text */}
      <section className="py-16 bg-white" data-testid="expert-services-section">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-6 leading-tight">
              Expert Plumbing &<br />Heating Services
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Our highly skilled technicians perform a variety of services, including backflow prevention; boilers—steam and hot water; commercial kitchens; faucet installation or repair; garbage disposals; gas piping; industrial piping; pipe thawing; piping replacement and repair; plumbing fixtures—replacement or repair; pumps; RPZ testing and rebuilding; reverse osmosis systems; sewage ejectors; sump pumps; toilets; washer/dryer hookups; water filters; water heaters; and water softeners.
            </p>
            <Link 
              to="/services" 
              className="inline-block bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-8 py-3 transition-colors"
            >
              Plumbing & Heating Solutions
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trusted Experts Section */}
      <section className="py-16 bg-[#14366C]" data-testid="trusted-experts-section">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
              Bruce Nelson Plumbing & Heating Services (BNPH) –<br />
              Trusted Plumbing & HVAC Experts in the Twin Cities Metro Area
            </h2>
            <p className="text-gray-300 max-w-4xl mx-auto leading-relaxed">
              BNPH is a locally owned and operated company proudly serving Minnesota with reliable, high-quality plumbing, heating, and cooling solutions. With over 50 years of experience, our licensed and insured technicians specialize in residential and commercial services, including water heater installation, furnace repair, fixture installations and replacements, boiler maintenance, and plumbing repairs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-4">
              Why Choose Us?
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3"
              >
                <CheckCircle className="w-5 h-5 text-[#F58220] flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-gray-600 mt-8 max-w-3xl mx-auto"
          >
            Contact BNPH today for expert service you can trust – proudly serving St. Paul, Minneapolis, Woodbury, Cottage Grove, Oakdale, Inver Grove Heights, Mendota Heights, Eagan, Bloomington, Eden Prairie, and surrounding communities.
          </motion.p>
        </div>
      </section>

      {/* Local Experts CTA */}
      <section className="py-16 bg-[#F8F8F8]" data-testid="local-experts-cta">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-6 leading-tight">
                Your Local Plumbing &<br />Heating Experts
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Serving St. Paul, Minneapolis, and the surrounding metro area, our experienced team is ready to handle your plumbing and heating needs. Call (651) 738-9354 today to schedule service or get expert assistance.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-8 py-3 transition-colors"
              >
                Request Service Now
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={ASSETS.team} 
                alt="BNPH Team" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Phone CTA Bar */}
      <section className="py-8 bg-[#14366C]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-white text-lg mb-4">Call us today to schedule your service!</p>
          <a 
            href="tel:651-738-9354" 
            className="inline-flex items-center gap-3 text-white hover:text-[#F58220] transition-colors"
          >
            <Phone className="w-8 h-8" />
            <span className="font-oswald text-3xl md:text-4xl font-bold">(651) 738-9354</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default OurCompany;
